import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { AttributeNode } from '@/types/schemas';
import { cn } from '@/lib';
import { isValidJsonLike, normalizeXhvValue } from '../utils/schema-utils';
import { XHV_ALLOWED_KEYS, XHV_KEY_LABEL, getXhvOptions, type XhvAllowedKey } from '@/domain/entities/property-schema';

export function CustomKeysEditor(props: {
  node: AttributeNode;
  updateNode: (patch: Partial<AttributeNode>) => void;
  disabled?: boolean;
}) {
  const { node, updateNode, disabled = false } = props;

  const canAddMore = XHV_ALLOWED_KEYS.some((k) => !(k in (node.x ?? {})));

  const [xDraft, setXDraft] = useState<Record<string, string>>({});
  const [xValidity, setXValidity] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const nextDraft: Record<string, string> = {};
    const nextValidity: Record<string, boolean> = {};
    const x = node.x ?? {};

    for (const [k, v] of Object.entries(x)) {
      const raw = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
      nextDraft[k] = raw;

      // If key has options, we don't care about JSON validity
      const opts = getXhvOptions(k as XhvAllowedKey);
      nextValidity[k] = opts ? true : isValidJsonLike(raw);
    }

    setXDraft(nextDraft);
    setXValidity(nextValidity);
  }, [node.id]);

  const addKey = () => {
    if (disabled) return;
    const base = node.x ?? {};

    const nextKey = XHV_ALLOWED_KEYS.find((k) => !(k in base));
    if (!nextKey) return;

    // default value: if options exist, pick first option; else empty string
    const opts = getXhvOptions(nextKey);
    const defaultValue = opts ? String(opts[0]) : '';

    setXDraft((p) => ({ ...p, [nextKey]: defaultValue }));
    setXValidity((p) => ({ ...p, [nextKey]: true }));
    updateNode({ x: { ...base, [nextKey]: defaultValue } });
  };

  const removeKey = (k: string) => {
    if (disabled) return;

    const next = { ...(node.x ?? {}) };
    delete next[k];

    setXDraft((prev) => {
      const n = { ...prev };
      delete n[k];
      return n;
    });
    setXValidity((prev) => {
      const n = { ...prev };
      delete n[k];
      return n;
    });

    updateNode({ x: Object.keys(next).length ? next : undefined });
  };

  const setValue = (k: string, value: string) => {
    if (disabled) return;

    setXDraft((prev) => ({ ...prev, [k]: value }));

    const opts = getXhvOptions(k as XhvAllowedKey);
    const valid = opts ? true : isValidJsonLike(value);
    setXValidity((prev) => ({ ...prev, [k]: valid }));

    const normalized = normalizeXhvValue(k, value);

    updateNode({ x: { ...(node.x ?? {}), [k]: normalized } });
  };

  return (
    <div className="space-y-3 rounded-xl border p-3">
      <div className="flex items-center justify-between">
        <Label>Custom keys (x-hv)</Label>

        <Button type="button" size="sm" variant="secondary" onClick={addKey} disabled={disabled || !canAddMore}>
          <Plus className="mr-2 h-4 w-4" />
          Add key
        </Button>
      </div>

      {!node.x || Object.keys(node.x).length === 0 ? (
        <p className="text-muted-foreground text-sm">No custom keys.</p>
      ) : (
        <div className="space-y-2">
          {Object.entries(node.x).map(([k]) => {
            const typedKey = k as XhvAllowedKey;
            const options = getXhvOptions(typedKey);

            return (
              <div key={k} className="grid gap-2 md:grid-cols-12 md:items-start">
                {/* Key select */}
                <div className="md:col-span-4">
                  <Select
                    value={k}
                    disabled={disabled}
                    onValueChange={(nextKey) => {
                      if (nextKey === k) return;

                      const nextX = { ...(node.x ?? {}) };
                      if (nextKey in nextX) return;

                      const oldVal = nextX[k];
                      delete nextX[k];

                      // if the new key is option-based and old value is invalid/empty, default it
                      const nextOpts = getXhvOptions(nextKey as XhvAllowedKey);
                      const oldValStr = typeof oldVal === 'string' ? oldVal : '';
                      const nextVal =
                        nextOpts && (!oldValStr || !nextOpts.includes(oldValStr)) ? nextOpts[0] : oldValStr;

                      nextX[nextKey] = nextVal;

                      setXDraft((prev) => {
                        const n = { ...prev };
                        n[nextKey] = String(nextVal ?? '');
                        delete n[k];
                        return n;
                      });

                      setXValidity((prev) => {
                        const n = { ...prev };
                        n[nextKey] = true;
                        delete n[k];
                        return n;
                      });

                      updateNode({ x: nextX });
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select key" />
                    </SelectTrigger>
                    <SelectContent>
                      {XHV_ALLOWED_KEYS.map((opt) => (
                        <SelectItem key={opt} value={opt} disabled={opt !== k && Boolean(node.x && opt in node.x)}>
                          {XHV_KEY_LABEL[opt]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground mt-1 text-xs">Key</p>
                </div>

                {/* Value editor (Select if options, else Textarea) */}
                <div className="md:col-span-7">
                  {options ? (
                    <Select
                      value={(xDraft[k] ?? (node.x?.[k] as string) ?? String(options[0])) as string}
                      disabled={disabled}
                      onValueChange={(v) => setValue(k, v)}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map((opt) => (
                          <SelectItem key={String(opt)} value={String(opt)}>
                            {String(opt)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Textarea
                      rows={2}
                      className={cn(
                        'resize-none font-mono text-xs',
                        xValidity[k] === false && 'border-red-500 focus-visible:ring-red-500',
                      )}
                      value={xDraft[k] ?? ''}
                      disabled={disabled}
                      onChange={(e) => setValue(k, e.target.value)}
                    />
                  )}

                  {!options && xValidity[k] === false ? (
                    <p className="mt-1 text-xs text-red-500">Invalid JSON</p>
                  ) : null}
                  <p className="text-muted-foreground mt-1 text-xs">
                    {options ? 'Value' : 'Value (raw JSON or string)'}
                  </p>
                </div>

                <div className="flex justify-end md:col-span-1">
                  <Button type="button" variant="outline" onClick={() => removeKey(k)} disabled={disabled}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
